<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    // Stores
    import { getInteractionStateStore } from '../../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../../itemsSessionStatusStore.js';
    import itemSessionStatus from '../../itemSessionStatus.js';
    // Components
    import { Input, Button, Slider, sliderValueFactory } from '@oat-sa-private/ui-elements';
    import Prompt from '../Prompt.svelte';
    import AtomicAriaLive from '../AtomicAriaLive.svelte';
    // Utils
    import { getContext, untrack } from 'svelte';
    import { generateElementId, __, ResizeObserver } from '@oat-sa-private/ui-core';
    import numberFormattingFactory, { getLocale } from '../../util/locale.js';
    import { createLastPressedKeyListener, wrapWithLogger } from '../util/analytics';
    import { getFlowDirection } from '../util/flowDirection.js';

    const qtiClass = 'qti-sliderInteraction';

    // response format
    const cardinality = 'single';

    let {
        baseType = 'integer', //or 'float', though 'float' is not supported properly

        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        // inherited item-level QTI attributes:
        language,
        id,
        dir,

        // inherited aria and data attributes:
        role,
        ariaAttrs = {},
        dataAttrs = {},

        // interaction-level QTI attributes:
        classes = '',
        prompt,
        disabled = false,
        lowerBound,
        upperBound,
        step = 1,
        orientation = 'horizontal', //or 'vertical'
        reverse = false //switch rendering of upperBound and lowerBound
    } = $props();

    let interactionElement = $state(); // bind to slider
    const getInteractionElement = () => interactionElement;

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();
    const writingMode = itemContext && itemContext.getWritingMode && itemContext.getWritingMode();
    let flowDir = $derived(getFlowDirection(dir, language, itemContext));

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let valueHelper, inputSize, vertical, isNormalFlow;

    setDerivedProps();

    function setDerivedProps() {
        lowerBound = Math.ceil(lowerBound);
        upperBound = Math.max(Math.floor(upperBound), lowerBound);

        valueHelper = sliderValueFactory(lowerBound, upperBound, step);

        inputSize = Math.max(lowerBound.toString().length, upperBound.toString().length);
        vertical = orientation === 'vertical';
        isNormalFlow = (!reverse && !vertical) || (reverse && vertical); //for vertical, lowerBound is on bottom and upperBound on top, which is against page flow
    }

    let isVerticalWritingMode = $derived(writingMode === 'vertical-rl');
    let isRtlFlow = $derived(flowDir === 'rtl' || isVerticalWritingMode);
    let contentDir = $derived(!vertical && isRtlFlow ? 'rtl' : undefined);
    let availableWidthPx = $state();

    $effect(() => {
        if (!isVerticalWritingMode || vertical || !interactionElement) {
            availableWidthPx = undefined;
            return;
        }

        const item = interactionElement.closest('.qti-item');
        if (!item) {
            availableWidthPx = undefined;
            return;
        }

        const gridRow = interactionElement.closest('.grid-row');
        const updateAvailableWidth = () => {
            const gridRowStyle = gridRow && getComputedStyle(gridRow);
            const horizontalPadding = gridRowStyle
                ? (Number.parseFloat(gridRowStyle.paddingLeft) || 0) +
                  (Number.parseFloat(gridRowStyle.paddingRight) || 0)
                : 0;
            const width = Math.floor(item.clientWidth - horizontalPadding);
            availableWidthPx = width > 0 ? width : undefined;
        };

        updateAvailableWidth();
        const resizeObserver = new ResizeObserver(updateAvailableWidth);
        resizeObserver.observe(item);
        gridRow && resizeObserver.observe(gridRow);
        return () => resizeObserver.disconnect();
    });

    let verticalRlLayoutStyle = $derived(
        isVerticalWritingMode && availableWidthPx
            ? `--slider-available-width: ${availableWidthPx}px`
            : undefined
    );

    const plusLabelledById = generateElementId('plus-label');
    const minusLabelledById = generateElementId('minus-label');
    const sliderLabelledById = generateElementId('slider-label');
    const inputDescribedById = generateElementId('input');
    const plusDescribedById = generateElementId('plus');
    const minusDescribedById = generateElementId('minus');
    const sliderDescribedById = generateElementId('slider');
    let ariaLiveAnnouncement = $state();

    let value = $state(null);
    let inputValue = $state('');
    let isInputFocused = false;
    let wasInputChangeFired = false;

    let isValueSet = $derived(value || value === 0);
    let disabledBySession = $derived($itemSessionStatusStore === itemSessionStatus.closed);

    // do initial response definition
    if (!interactionStateStore.hasResponse()) {
        interactionStateStore.merge({ qtiClass });
        saveResponse();
    }

    // load the response when the store changes
    loadResponse();
    $effect(() => {
        if ($interactionStateStore) {
            untrack(() => loadResponse());
        }
    });

    const lastPressedKeyListener = createLastPressedKeyListener();
    const handleKeyDown = lastPressedKeyListener.saveLastPressedKey;

    /**
     * Loads response from store and set value
     */
    function loadResponse() {
        const storedValue = interactionStateStore.getResponseValue();
        if (typeof storedValue === 'number') {
            value = storedValue;
            //do not always restore inputValue: response is updated when typing text, but text itself only on blur
            if (!inputValue || !isInputFocused) {
                inputValue = value;
            }
        } else {
            //restore inputValue only if loadResponse overwrites existing 'value': if it had invalid text, it should be kept
            if (value || value === 0) {
                inputValue = '';
            }
            value = null;
        }
    }

    /**
     * Set response to the store
     */
    function saveResponse() {
        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value: $state.snapshot(value)
            },
            isValueSet ? value <= upperBound && value >= lowerBound : true
        );
    }

    /**
     * Parse number which user had entered in text input
     * @param {String} str
     * @returns {Number}
     */
    function parseNumber(str) {
        const numberParser = numberFormattingFactory(getLocale());
        return numberParser.parseIntValue(str);
    }

    /**
     * Handle slider change
     * @param {CustomEvent} e
     */
    const handleSliderChange = wrapWithLogger({
        /// use a getter instead of direct interactionElement,
        // as passing interactionElement as null
        // seals null value forever for a resulted function
        getInteractionElement,
        handler(e) {
            value = e.detail.value;

            inputValue = value;
            saveResponse();
        },
        eventTypeToDomEventTypeMap: {
            mousedown: 'click'
        },
        interactionStateStore,
        getDetails: e => ({
            ...(e.detail.position && { position: e.detail.position })
        }),
        logDebounceOptions: {
            wait: 300,
            leading: true,
            trailing: true
        }
    });

    /**
     * Handle minus button click
     * @param {CustomEvent}
     */
    const handleMinusClick = wrapWithLogger({
        getInteractionElement,
        handler() {
            value = valueHelper.increment(value, -step);

            inputValue = value;
            saveResponse();

            liveAnnounce(value);
        },
        getDetails: e => ({
            ...(lastPressedKeyListener.lastPressedKey && { pressedKey: lastPressedKeyListener.lastPressedKey }),
            ...(e.detail.position && !lastPressedKeyListener.lastPressedKey && { position: e.detail.position })
        }),
        interactionStateStore
    });

    /**
     * Handle plus button click
     * @param {CustomEvent}
     */
    const handlePlusClick = wrapWithLogger({
        getInteractionElement,
        handler() {
            value = valueHelper.increment(value, step);

            inputValue = value;
            saveResponse();

            liveAnnounce(value);
        },
        getDetails: e => ({
            ...(lastPressedKeyListener.lastPressedKey && { pressedKey: lastPressedKeyListener.lastPressedKey }),
            ...(e.detail.position && !lastPressedKeyListener.lastPressedKey && { position: e.detail.position })
        }),
        interactionStateStore
    });

    /**
     * Handle text input change
     * @param {CustomEvent}
     */
    const handleTextChange = wrapWithLogger({
        getInteractionElement,
        handler(e) {
            wasInputChangeFired = true;
            inputValue = e.detail.value;
            const numericInputValue = parseNumber(inputValue);
            if (!isNaN(numericInputValue)) {
                value = valueHelper.adjust(numericInputValue);

                saveResponse();
            } else {
                value = null;
                saveResponse();
            }
        },
        getDetails: e => ({
            pressedKey: lastPressedKeyListener.lastPressedKey,
            target: e.detail.target
        }),
        interactionStateStore
    });

    /**
     * Handle input container focusin (equals input focus)
     */
    function handleInputContainerFocusin() {
        isInputFocused = true;
        wasInputChangeFired = false;
    }

    /**
     * Handle input container focusout (equals input blur)
     */
    function handleInputContainerFocusout() {
        isInputFocused = false;

        if (isValueSet) {
            inputValue = value;
        }

        if (wasInputChangeFired) {
            wasInputChangeFired = false;
            if (isValueSet) {
                liveAnnounce(__('Changed to %d', value));
            } else {
                liveAnnounce(__('Changed to default'));
            }
        }
    }

    /**
     * Get a string describing value, min and max, to use as part of aria-label
     * @returns {String}
     */
    function getStatusAriaLabel() {
        const valueAria = isValueSet ? __('Current value %d', value) : __('Default');
        return __('%s, min %d max %d', valueAria, lowerBound, upperBound);
    }

    /**
     * Sets the aria-live announcement
     * @param {String} text
     */
    function liveAnnounce(text) {
        ariaLiveAnnouncement = { text };
    }
</script>

<style>
    @add-mixin dir-style .content-container, --dir-rtl-multiplier, 1, -1;

    .qti-sliderInteraction.vertical-rl-layout,
    .qti-sliderInteraction.vertical-rl-layout > .content-container {
        box-sizing: border-box;
        width: var(--slider-available-width);
        max-width: var(--slider-available-width);
    }

    .content-container {
        writing-mode: horizontal-tb;
        box-sizing: border-box;
        min-width: 0;
        max-width: 100%;
        display: flex;
        flex-wrap: wrap;
        align-items: center;

        & :global(button) {
            /* user tends to press several times on plus/minus buttons, which can get recognized as double-touch-to-zoom */
            touch-action: manipulation;
            margin: 0;

            &:last-of-type {
                order: 3;
            }
        }
    }

    .input-container {
        margin-inline-end: 2.5rem;

        /* override usual input styles */
        & :global(input) {
            @add-mixin double-border var(--color-border-default);
        }
        & :global(input.height-medium) {
            padding-inline-end: 2rem;
        }
        & :global(.icon) {
            display: none;
        }
    }

    .slider-container {
        flex: 1 1 auto;
        min-width: 0;
        margin: 0 2rem;
    }

    /* mobile and tablet portrait, if horizontal
     [note: would be more logical to measure against container column] */
    @media screen and (--mq-maxwidth-large) {
        .content-container:not(.vertical) {
            & .input-container {
                flex-grow: 1;
            }

            & :global(button:first-of-type) {
                margin-inline-end: 2rem;
            }

            & :global(button:last-of-type) {
                order: 0;
            }

            & .slider-container {
                flex-basis: 100%;
                margin: 3rem 0 0 0;
            }
        }
    }

    /* vertical orientation */
    .vertical {
        &.content-container {
            display: grid;
            grid-template-columns: min-content;
            justify-items: end;
        }
        & .input-container {
            margin-inline-end: 0;
            margin-bottom: 3rem;
            /* 3rem is the part of slider width to the right of the track */
            transform: translateX(calc(var(--dir-rtl-multiplier) * (50% - 3rem)));
        }
        & .slider-container {
            height: 35vh;
            margin: 2rem 0;
        }
    }

    :global([data-layouts~='hideFeedbacksLayout']) {
        & .qti-sliderInteraction :global(.feedback-inline) {
            display: none;
        }
    }
</style>

<div
    bind:this={interactionElement}
    onkeydown={handleKeyDown}
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    class:vertical-rl-layout={isVerticalWritingMode}
    style={verticalRlLayoutStyle}
    lang={language}
    {id}
    {dir}
    {role}
    aria-disabled={disabled || disabledBySession}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt}
        <Prompt blockTree={prompt} />
    {/if}

    <AtomicAriaLive lang={instructionsLang} announcement={ariaLiveAnnouncement} />
    <div class="hidden" lang={instructionsLang}>
        <div id={plusLabelledById}>{__('Plus. %s', getStatusAriaLabel(isValueSet, value))}</div>
        <div id={minusLabelledById}>{__('Minus. %s', getStatusAriaLabel(isValueSet, value))}</div>
        <div id={inputDescribedById}>{__('To enter the slider value in this field, type.')}</div>
        <div id={minusDescribedById}>{__('Use to decrease the slider value by %d', step)}</div>
        <div id={plusDescribedById}>{__('Use to increase the slider value by %d', step)}</div>
        <div id={sliderDescribedById}>{__('Use the arrow keys to increase or decrease the value by %d', step)}</div>
    </div>
    <div
        class="content-container"
        class:inverted-flow={!isNormalFlow}
        class:vertical
        {...(contentDir ? { dir: contentDir } : {})}>
        <div class="input-container" onfocusin={handleInputContainerFocusin} onfocusout={handleInputContainerFocusout}>
            <label>
                <span class="visually-hidden">{getStatusAriaLabel(isValueSet, value)}</span>
                <Input
                    placeholder="-"
                    value={inputValue}
                    height="medium"
                    size={inputSize}
                    inputmode="numeric"
                    readonly={disabled || disabledBySession}
                    feedback="inline"
                    feedbackProps={{ showValidFeedback: false }}
                    customValidity={{ valid: isValueSet || !inputValue }}
                    ariaDescribedBy={inputDescribedById}
                    on:change={handleTextChange} />
            </label>
        </div>
        {#if isNormalFlow}
            <Button
                ariaLabelledBy={minusLabelledById}
                ariaDescribedBy={minusDescribedById}
                skin="secondary"
                size="medium"
                icon="minus-16"
                shape="circular"
                disabled={disabled || disabledBySession}
                dataTestId="slider-minus"
                on:click={handleMinusClick} />
            <Button
                ariaLabelledBy={plusLabelledById}
                ariaDescribedBy={plusDescribedById}
                skin="secondary"
                size="medium"
                icon="plus-16"
                shape="circular"
                disabled={disabled || disabledBySession}
                dataTestId="slider-plus"
                on:click={handlePlusClick} />
        {:else}
            <Button
                ariaLabelledBy={plusLabelledById}
                ariaDescribedBy={plusDescribedById}
                skin="secondary"
                size="medium"
                icon="plus-16"
                shape="circular"
                disabled={disabled || disabledBySession}
                dataTestId="slider-plus"
                on:click={handlePlusClick} />
            <Button
                ariaLabelledBy={minusLabelledById}
                ariaDescribedBy={minusDescribedById}
                skin="secondary"
                size="medium"
                icon="minus-16"
                shape="circular"
                disabled={disabled || disabledBySession}
                dataTestId="slider-minus"
                on:click={handleMinusClick} />
        {/if}
        <div class="slider-container">
            <Slider
                min={lowerBound}
                max={upperBound}
                {step}
                {value}
                {reverse}
                {vertical}
                disabled={disabled || disabledBySession}
                ariaLabelledBy={sliderLabelledById}
                ariaDescribedBy={sliderDescribedById}
                on:change={handleSliderChange} />
            <span id={sliderLabelledById} lang={instructionsLang} class="hidden">
                {getStatusAriaLabel(isValueSet, value)}
            </span>
        </div>
    </div>
</div>
